import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@/lib/utils';

// template — DWR right aside. Sidebar sticky destra, container per <StepRail>/<LevelsRail>.
// Sfondo paper-soft, bordo sinistra rule.
//
// **FI-53 (Sprint F 2026-05-21)** — Hide su mobile. Pre-fix l'aside aveva
// fallback strip orizzontale sopra il main (`max-[900px]:order-first ...`)
// che occupava verticali preziose e non era sticky → scrollando giù spariva,
// richiedeva scroll-to-top per riaccedere. Post-fix: su mobile (<900px)
// l'aside è completamente nascosto; il suo contenuto vive in un
// `<MobileAsideDrawer>` attivato dal pulsante menu nella topbar. Su desktop
// (≥900px) comportamento invariato.
//
// **PR 11**: prop opzionale `watermarkUrl` per il pattern `.aside-watermark`
// mockup (styles.css 226-242). Quando presente, mostra l'artwork della classe
// come sfondo decorativo dell'aside (opacity 0.08, background center 30%
// contain). Pass-through da BuilderPage / CharacterViewPage / LevelUpFlowPage
// con URL /classes/{classId}.png.

interface AsideProps {
  children: ReactNode;
  className?: string;
  /** URL dell'artwork classe da mostrare come watermark di sfondo. */
  watermarkUrl?: string | undefined;
}

interface AsideStyle extends CSSProperties {
  '--wm-image'?: string;
  '--wm-opacity'?: number;
}

export function Aside({ children, className, watermarkUrl }: AsideProps) {
  const style: AsideStyle | undefined = watermarkUrl
    ? { '--wm-image': `url("${watermarkUrl}")`, '--wm-opacity': 0.08 }
    : undefined;
  return (
    <aside
      style={style}
      className={cn(
        'bg-paper-soft border-rule sticky top-14 isolate max-h-[calc(100vh-56px)] self-start overflow-x-hidden overflow-y-auto border-l px-4 py-5',
        'max-[900px]:hidden',
        className,
      )}
    >
      {watermarkUrl ? <div aria-hidden className="aside-watermark" /> : null}
      {children}
    </aside>
  );
}
